import { useEffect, useState } from 'react';

import StarIcon from '@mui/icons-material/Star';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ConfirmDialog } from 'src/components/ConfimDialog';
import SubTitle from 'src/components/UI/Subtitle';
import TabTemplate from 'src/components/UI/TabTemplate';
import { getFavoritesWithAlerts, removeFavorite } from 'src/services/favorite';
import { setBannerMessage } from 'src/store/display';
import { Alert } from 'src/types/alert';
import { Favorite } from 'src/types/favorite';
import { ScrollToTop } from 'src/utils/scroll';

import { DefaultTable } from './Tables/default';
import { OperationTable } from './Tables/operation';
import { FavoritesTabs } from './Tabs';

const tabs = [
  { name: 'operation', label: 'Operações', singular: 'Operação' },
  { name: 'institution', label: 'Instituições', singular: 'Instituição' },
  { name: 'professional', label: 'Profissionais', singular: 'Profissional' },
  { name: 'article', label: 'Artigos', singular: 'Artigo' },
  { name: 'event', label: 'Eventos', singular: 'Evento' },
  { name: 'service', label: 'Serviços', singular: 'Serviço' },
];

export function Favorites() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Favorite | null>(null);
  const [dialogTitle, setDialogTitle] = useState('');
  const [favoriteData, setFavoriteData] = useState<
    {
      favorite: Favorite;
      alerts: Alert[];
    }[]
  >([]);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { isLoading, refetch } = useQuery({
    queryKey: ['favorites', tabs[selectedTab].name],
    queryFn: async () =>
      await getFavoritesWithAlerts({
        collectionName: tabs[selectedTab].name,
        sort: ['createdAt:asc'],
      }),
    onSuccess: (data) => {
      setFavoriteData(data.data);
    },
    refetchOnWindowFocus: true,
    enabled: !!tabs[selectedTab].name,
  });

  // Efeito para limpar os dados quando a aba é alterada
  useEffect(() => {
    setFavoriteData([]);
  }, [selectedTab]);

  const removeFavoriteMutation = useMutation(removeFavorite, {
    onSuccess: () => {
      setOpenDialog(false);
      queryClient.invalidateQueries({
        queryKey: ['favorites', tabs[selectedTab].name],
      });
      dispatch(
        setBannerMessage({
          message: 'Item removido da lista de favoritos com sucesso',
          type: 'success',
          autoclose: true,
        }),
      );
    },
    onError: () => {
      dispatch(
        setBannerMessage({
          message: 'Ocorreu um erro ao desfavoritar, tente novamente',
          type: 'error',
          autoclose: false,
        }),
      );
    },
  });

  function handleTabChange(tab: number) {
    setSelectedTab(tab);
  }

  function handleRemoveFavorite(favorite: Favorite | null) {
    if (favorite) {
      setSelectedItem(favorite);
      removeFavoriteMutation.mutate(favorite.id);
    }
  }

  function handleClickRow(favorite: Favorite, title: string) {
    setSelectedItem(favorite);
    setDialogTitle(title);
    setOpenDialog(true);
  }

  return (
    <ScrollToTop>
      <TabTemplate
        title="Meus favoritos"
        titleIcon={<StarIcon />}
        tabContent={[]}
        tabIndex={0}
        handleChange={() => {}}
        breadcrumbs1="Meus favoritos"
      />
      <Paper elevation={2} sx={{ marginTop: 4, padding: '56px 30px 10px' }}>
        <SubTitle title="Favoritos" />
        <Box>
          <FavoritesTabs
            selectedTab={selectedTab}
            setSelectedTab={handleTabChange}
            tabs={tabs}
          />
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%">
              <CircularProgress size="1.8rem" color="primary" />
            </Box>
          ) : (
            <Box>
              {tabs[selectedTab].name === 'operation' ? (
                <OperationTable
                  data={favoriteData}
                  onRowClick={handleClickRow}
                  refetch={refetch}
                  isLoading={isLoading}
                />
              ) : (
                <DefaultTable
                  favoriteType={tabs[selectedTab]}
                  data={favoriteData.map((item) => ({
                    favorite: item.favorite,
                  }))}
                  onRowClick={handleClickRow}
                  refetch={refetch}
                  isLoading={isLoading}
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>
      <ConfirmDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={'Remoção do Favorito'}
        submitButtonColor="inherit"
        submitButtonTextVariant="outlined"
        cancelButtonColor="error"
        cancelButtonTextVariant="contained"
        subTitle={
          tabs[selectedTab].name === 'operation' ? (
            <Typography
              fontSize={12}
              fontWeight={400}
              color="#1D1D1D"
              component="span">
              Tem certeza que deseja remover o favorito <b>{dialogTitle}</b>?
              <br />
              Ao remover este favorito, todos os alertas vinculados a ele serão
              desativados.
              <br />
            </Typography>
          ) : (
            <Typography
              fontSize={12}
              fontWeight={400}
              color="#1D1D1D"
              component="span">
              Tem certeza que deseja remover o favorito <b>{dialogTitle}</b>?
            </Typography>
          )
        }
        hasActionButton={true}
        submitAction={() => handleRemoveFavorite(selectedItem)}
      />
    </ScrollToTop>
  );
}
